import { useEffect, useState, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Loader2, Search, X, ArrowUpDown, ArrowUp, ArrowDown, CalendarIcon, Copy, Check, Users, Tag, BookOpen, Clock, Coins } from 'lucide-react';
import { format } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { useTestClock } from '@/contexts/TestClockContext';
import { cn } from '@/lib/utils';
import { safeLog } from '@/utils/safeLogger';

interface Transaction {
  id: string;
  user_email: string;
  action_type: string;
  baseline_credits: number;
  openai_usage_credits: number;
  final_credits: number;
  credits_before: number;
  credits_after: number;
  transaction_id: string;
  reference_id: string | null;
  created_at: string;
  context_metadata?: {
    features?: {
      extraction?: boolean;
      smartCategories?: boolean;
      smartNotes?: boolean;
    };
    totalTokens?: number;
    model?: string;
  };
}

export function CreditTransactionsLog() {
  const { isTestMode, getCurrentTime } = useTestClock();
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalCredits, setTotalCredits] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const { toast } = useToast();
  
  // Search & Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [featureFilter, setFeatureFilter] = useState<string>('all');
  const [minCredits, setMinCredits] = useState<string>('');
  const [maxCredits, setMaxCredits] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  
  // Sort State
  const [sortField, setSortField] = useState<keyof Transaction>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    loadTransactions();
    
    // Subscribe to realtime changes
    const channel = supabase
      .channel('credit-transactions-changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'credit_transactions'
        },
        (payload) => {
          safeLog.info('[Realtime] New transaction received');
          // Reload transactions to get latest data
          loadTransactions();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  async function loadTransactions() {
    try {
      setLoading(true);

      // Get current session token
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        safeLog.error('No session found');
        return;
      }

      // Call edge function to get transactions with user emails
      const { data, error } = await supabase.functions.invoke('get-credit-transactions', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        safeLog.error('Error calling edge function', error as Error);
        throw error;
      }

      const txns = data?.transactions || [];
      
      safeLog.info('[CreditTransactionsLog] Received transactions', { count: txns.length });

      if (txns.length === 0) {
        setTransactions([]);
        setTotalCredits(0);
        return;
      }

      // Filter out top-ups and subscriptions - only show credit usage
      const usageTransactions = txns.filter((t: Transaction) => 
        t.action_type === 'contact_processing' || 
        (!t.action_type.includes('topup') && !t.action_type.includes('subscription'))
      );

      setTransactions(usageTransactions);

      // Calculate total credits used
      const total = usageTransactions.reduce((sum: number, t: Transaction) => sum + t.final_credits, 0);
      setTotalCredits(total);

    } catch (error) {
      safeLog.error('Error loading transactions', error as Error);
    } finally {
      setLoading(false);
    }
  }

  const getFeatureBadges = (features?: { extraction?: boolean; smartCategories?: boolean; smartNotes?: boolean }) => {
    if (!features) return null;
    
    const badges = [];
    if (features.extraction) badges.push({ label: 'Contact Extracted', variant: 'default' as const, icon: 'Users' });
    if (features.smartCategories) badges.push({ label: 'Smart Categories', variant: 'secondary' as const, icon: 'Tag' });
    if (features.smartNotes) badges.push({ label: 'Smart Notes', variant: 'outline' as const, icon: 'BookOpen' });
    
    return badges;
  };

  // Filter and Sort Logic
  const filteredAndSorted = useMemo(() => {
    let result = [...transactions];
    
    // Apply search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(item => 
        item.user_email.toLowerCase().includes(term) ||
        item.action_type.toLowerCase().includes(term) ||
        (item.transaction_id && item.transaction_id.toLowerCase().includes(term)) ||
        (item.reference_id && item.reference_id.toLowerCase().includes(term))
      );
    }
    
    // Apply feature filter
    if (featureFilter !== 'all') {
      result = result.filter(item => {
        const features = item.context_metadata?.features;
        if (!features) return false;
        
        switch (featureFilter) {
          case 'extraction_only':
            return features.extraction && !features.smartCategories && !features.smartNotes;
          case 'with_categories':
            return features.extraction && features.smartCategories;
          case 'with_notes':
            return features.extraction && features.smartNotes;
          case 'all_features':
            return features.extraction && features.smartCategories && features.smartNotes;
          default:
            return true;
        }
      });
    }
    
    // Apply credits range filter
    if (minCredits) {
      const min = parseFloat(minCredits);
      if (!isNaN(min)) {
        result = result.filter(item => item.final_credits >= min);
      }
    }
    if (maxCredits) {
      const max = parseFloat(maxCredits);
      if (!isNaN(max)) {
        result = result.filter(item => item.final_credits <= max);
      }
    }
    
    // Apply date range filter
    if (dateFrom) {
      result = result.filter(item => new Date(item.created_at) >= dateFrom);
    }
    if (dateTo) {
      const endOfDay = new Date(dateTo);
      endOfDay.setHours(23, 59, 59, 999);
      result = result.filter(item => new Date(item.created_at) <= endOfDay);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      // Special case for AI Tokens (context_metadata)
      if (sortField === 'context_metadata') {
        const aTokens = a.context_metadata?.totalTokens ?? 0;
        const bTokens = b.context_metadata?.totalTokens ?? 0;
        return (aTokens - bTokens) * direction;
      }
      
      const aVal = a[sortField];
      const bVal = b[sortField];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return aVal.localeCompare(bVal) * direction;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return (aVal - bVal) * direction;
      }
      return 0;
    });
    
    return result;
  }, [transactions, searchTerm, featureFilter, minCredits, maxCredits, dateFrom, dateTo, sortField, sortDirection]);

  // Calculate filtered credits sum
  const filteredCreditsSum = useMemo(() => {
    return filteredAndSorted.reduce((sum, t) => sum + t.final_credits, 0);
  }, [filteredAndSorted]);

  const handleSort = (field: keyof Transaction) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFeatureFilter('all');
    setMinCredits('');
    setMaxCredits('');
    setDateFrom(undefined);
    setDateTo(undefined);
  };

  const SortIcon = ({ field }: { field: keyof Transaction }) => {
    if (sortField !== field) return <ArrowUpDown className="ml-2 h-4 w-4" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="ml-2 h-4 w-4" /> : 
      <ArrowDown className="ml-2 h-4 w-4" />;
  };

  const activeFiltersCount = [
    searchTerm,
    featureFilter !== 'all',
    minCredits,
    maxCredits,
    dateFrom,
    dateTo
  ].filter(Boolean).length;

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
      toast({
        title: "Copied",
        description: "Transaction ID copied to clipboard",
        duration: 2000,
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Could not copy to clipboard",
        variant: "destructive",
      });
    }
  };

  return (
    <Card className="glass-card-strong hover-lift transition-all">
      <CardHeader>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500/10 to-amber-500/10 flex items-center justify-center animate-float">
            <Coins className="h-5 w-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-gradient">Credit Transactions</CardTitle>
            <CardDescription>
              All operations that consumed credits (all users)
            </CardDescription>
          </div>
        </div>
        
        {isTestMode && (
          <Alert className="mt-4 border-blue-500 bg-blue-50 dark:bg-blue-950">
            <Clock className="h-4 w-4 text-blue-600" />
            <AlertDescription>
              <strong>🧪 Test Mode Active:</strong> All timestamps are relative to simulated time.
              <br />
              <span className="text-xs">
                Current simulated time: {format(getCurrentTime(), 'PPP HH:mm:ss')}
              </span>
            </AlertDescription>
          </Alert>
        )}
        
        {/* Filter Controls */}
        <div className="flex flex-col gap-3 mt-4">
          <div className="flex flex-wrap gap-2">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by User, Action, Transaction ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8"
              />
            </div>
            
            <Select value={featureFilter} onValueChange={setFeatureFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Feature Combination" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Combinations</SelectItem>
                <SelectItem value="extraction_only">Extraction Only</SelectItem>
                <SelectItem value="with_categories">+ Smart Categories</SelectItem>
                <SelectItem value="with_notes">+ Smart Notes</SelectItem>
                <SelectItem value="all_features">All Features</SelectItem>
              </SelectContent>
            </Select>
            
            <Input
              type="number"
              placeholder="Min Credits"
              value={minCredits}
              onChange={(e) => setMinCredits(e.target.value)}
              className="w-[120px]"
              step="0.01"
            />
            
            <Input
              type="number"
              placeholder="Max Credits"
              value={maxCredits}
              onChange={(e) => setMaxCredits(e.target.value)}
              className="w-[120px]"
              step="0.01"
            />
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[180px] justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateFrom ? format(dateFrom, 'dd.MM.yyyy') : 'From Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateFrom}
                  onSelect={setDateFrom}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-[180px] justify-start text-left font-normal">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dateTo ? format(dateTo, 'dd.MM.yyyy') : 'To Date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={dateTo}
                  onSelect={setDateTo}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            
            {activeFiltersCount > 0 && (
              <Button variant="ghost" onClick={resetFilters}>
                <X className="mr-2 h-4 w-4" />
                Reset Filters ({activeFiltersCount})
              </Button>
            )}
          </div>
          
          {/* Result Count and Filtered Sum */}
          <div className="flex justify-between items-center mt-4">
            <p className="text-sm text-muted-foreground">
              {filteredAndSorted.length} of {transactions.length} entries
              {filteredAndSorted.length !== transactions.length && ' (filtered)'}
            </p>
            {filteredAndSorted.length !== transactions.length && (
              <p className="text-sm font-semibold text-destructive">
                Filtered Credits: {filteredCreditsSum.toFixed(2)}
              </p>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : transactions.length === 0 ? (
          <p className="text-center py-8 text-muted-foreground">
            No credit transactions found
          </p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center">
                      Date/Time
                      <SortIcon field="created_at" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('user_email')}
                  >
                    <div className="flex items-center">
                      User
                      <SortIcon field="user_email" />
                    </div>
                  </TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('credits_before')}
                  >
                    <div className="flex items-center justify-end">
                      Before
                      <SortIcon field="credits_before" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('final_credits')}
                  >
                    <div className="flex items-center justify-end">
                      Amount
                      <SortIcon field="final_credits" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('credits_after')}
                  >
                    <div className="flex items-center justify-end">
                      After
                      <SortIcon field="credits_after" />
                    </div>
                  </TableHead>
                  <TableHead 
                    className="text-right cursor-pointer select-none hover:bg-muted/50"
                    onClick={() => handleSort('context_metadata')}
                  >
                    <div className="flex items-center justify-end whitespace-nowrap">
                      AI Tokens
                      <SortIcon field="context_metadata" />
                    </div>
                  </TableHead>
                  <TableHead>Transaction ID</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAndSorted.map((txn) => (
                  <TableRow key={txn.id}>
                    <TableCell className={cn(
                      "whitespace-nowrap",
                      isTestMode && "text-blue-600 dark:text-blue-400 font-medium"
                    )}>
                      {new Date(txn.created_at).toLocaleString('de-DE')}
                      {isTestMode && <span className="text-xs ml-1">(sim)</span>}
                    </TableCell>
                    <TableCell className="font-medium">{txn.user_email}</TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {getFeatureBadges(txn.context_metadata?.features)?.map((badge, idx) => {
                          const Icon = badge.icon === 'Users' ? Users : badge.icon === 'Tag' ? Tag : BookOpen;
                          return (
                            <Badge key={idx} variant={badge.variant} className="text-xs gap-1">
                              <Icon className="h-3 w-3" />
                              {badge.label}
                            </Badge>
                          );
                        }) || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {txn.credits_before?.toFixed(2) ?? '—'}
                    </TableCell>
                    <TableCell className={cn(
                      "text-right font-semibold tabular-nums",
                      txn.final_credits < 0 ? "text-destructive" : "text-green-600"
                    )}>
                      {txn.final_credits > 0 ? '+' : ''}{txn.final_credits.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {txn.credits_after?.toFixed(2) ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm font-mono">
                      {txn.context_metadata?.totalTokens 
                        ? txn.context_metadata.totalTokens.toLocaleString() 
                        : '—'}
                    </TableCell>
                    <TableCell>
                      {txn.transaction_id ? (
                        <div className="flex items-center gap-2">
                  <code className="text-xs font-mono text-muted-foreground">
                    {txn.transaction_id.substring(0, 6)}...
                  </code>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => copyToClipboard(txn.transaction_id!, txn.id)}
                          >
                            {copiedId === txn.id ? (
                              <Check className="h-3 w-3 text-green-600" />
                            ) : (
                              <Copy className="h-3 w-3" />
                            )}
                          </Button>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
